/**
 * Query Comparison — a top-level admin tab. Runs two statements (A / B) on ONE
 * connection: interleaved N times for fair timing, EXPLAIN for each, a
 * Handler_* session-counter delta, and an order-independent equivalence check
 * inside a single read-only snapshot. Prove a rewrite is faster AND equivalent.
 *
 * The two statements are run as-is (same trust boundary as the query editor)
 * and both are audit-logged. Props: { active }.
 */
import { createSignal, createResource, createMemo, For, Show } from 'solid-js';
import { api } from '../lib/api.js';
import CodeMirrorSQL from '../components/CodeMirrorSQL.jsx';
import QueryProfiler from '../components/QueryProfiler.jsx';
import { runQueryCompare } from '../lib/querycompare.js';
import { toast } from '../lib/toast.js';
import { sqlSchemaFor } from '../lib/schema.js';

const SYSTEM_DBS = ['mysql', 'information_schema', 'performance_schema', 'sys'];
const fmtMs = (v) => (v == null ? '—' : v < 10 ? v.toFixed(2) : v < 100 ? v.toFixed(1) : Math.round(v).toLocaleString());

export default function QueryComparePanel() {
  const [conns] = createResource(async () => {
    const { connections } = await api.get('/api/connections');
    return connections ?? [];
  });
  const [connId, setConnId] = createSignal('');
  const [dbList, setDbList] = createSignal(null);
  const [dbLoading, setDbLoading] = createSignal(false);
  const [database, setDatabase] = createSignal('');
  const sqlSchema = sqlSchemaFor(connId, database); // editor autocomplete
  const [sqlA, setSqlA] = createSignal('');
  const [sqlB, setSqlB] = createSignal('');
  const [runs, setRuns] = createSignal(5);
  const [running, setRunning] = createSignal(false);
  const [result, setResult] = createSignal(null);
  const [err, setErr] = createSignal('');
  const [profileSql, setProfileSql] = createSignal(null); // open the shared profiler for one side

  const loadDbs = async (id) => {
    if (!id) { setDbList(null); return; }
    setDbLoading(true);
    try {
      const { databases } = await api.get(`/api/connections/${id}/databases`);
      setDbList((databases ?? []).map((d) => d.name).filter((n) => !SYSTEM_DBS.includes(n)));
    } catch (e) { setErr(e.message); setDbList([]); }
    finally { setDbLoading(false); }
  };
  const onConnChange = (id) => { setConnId(id); setDatabase(''); setResult(null); loadDbs(id); };

  const run = async () => {
    if (!connId()) { setErr('Pick a connection first.'); return; }
    if (!sqlA().trim() || !sqlB().trim()) { setErr('Both query A and query B are required.'); return; }
    setErr(''); setRunning(true); setResult(null);
    try {
      const r = await runQueryCompare(connId(), database(), sqlA(), sqlB(), runs());
      setResult(r);
      if (r.a?.error) toast.error(`Query A failed: ${r.a.error}`);
      if (r.b?.error) toast.error(`Query B failed: ${r.b.error}`);
    } catch (e) { setErr(e.message); }
    finally { setRunning(false); }
  };

  // Which side is faster by median (only when both ran).
  const faster = createMemo(() => {
    const r = result();
    if (!r || r.a?.error || r.b?.error) return '';
    if (r.a.median === r.b.median) return 'tie';
    return r.a.median < r.b.median ? 'a' : 'b';
  });
  const speedup = createMemo(() => {
    const r = result();
    if (!r || faster() === '' || faster() === 'tie') return '';
    const lo = Math.min(r.a.median, r.b.median), hi = Math.max(r.a.median, r.b.median);
    if (!lo) return '';
    return (hi / lo).toFixed(2) + '×';
  });

  // Union of Handler keys present on either side, for the delta table.
  const handlerKeys = createMemo(() => {
    const r = result();
    if (!r) return [];
    const s = new Set([...Object.keys(r.a?.handler || {}), ...Object.keys(r.b?.handler || {})]);
    return [...s].sort();
  });

  return (
    <div class="qc-panel">
      <div class="qc-head">
        <div>
          <h2>Query Comparison</h2>
          <p class="muted">Run two statements side-by-side — timing, EXPLAIN, Handler deltas, and result equivalence.</p>
        </div>
      </div>

      <div class="qc-controls">
        <label class="cr-field">
          <span class="cr-field-label">Connection</span>
          <select class="input" value={connId()} onChange={(e) => onConnChange(e.currentTarget.value)}>
            <option value="">— pick a connection —</option>
            <For each={conns() ?? []}>{(c) => <option value={c.id}>{c.name}</option>}</For>
          </select>
        </label>
        <label class="cr-field">
          <span class="cr-field-label">Database</span>
          <select class="input" value={database()} onChange={(e) => setDatabase(e.currentTarget.value)}
                  disabled={dbLoading() || !dbList()}>
            <option value="">{dbLoading() ? 'loading…' : '(none)'}</option>
            <For each={dbList() ?? []}>{(n) => <option value={n}>{n}</option>}</For>
          </select>
        </label>
        <label class="cr-field qc-runs">
          <span class="cr-field-label">Runs each</span>
          <input class="input" type="number" min="1" max="25" value={runs()}
                 onInput={(e) => setRuns(Math.max(1, Math.min(25, Number(e.currentTarget.value) || 1)))} />
        </label>
        <div class="qc-run-btn">
          <button class="btn" onClick={run} disabled={running() || !connId()}>
            {running() ? '⏳ Comparing…' : '⚖ Compare'}
          </button>
        </div>
      </div>

      <Show when={err()}><div class="form-error" style="margin:8px 0;">{err()}</div></Show>

      <div class="qc-editors">
        <div class="qc-editor">
          <div class="qc-editor-label qc-label-a">Query A
            <button class="btn btn-sm btn-ghost qc-profile-btn" disabled={!connId() || !sqlA().trim()}
                    onClick={() => setProfileSql(sqlA())} title="Profile query A — per-stage time breakdown">⏱ Profile</button>
          </div>
          <CodeMirrorSQL value={sqlA()} onInput={setSqlA} maxHeight="200px" lineWrap={false} schema={sqlSchema} />
        </div>
        <div class="qc-editor">
          <div class="qc-editor-label qc-label-b">Query B
            <button class="btn btn-sm btn-ghost qc-profile-btn" disabled={!connId() || !sqlB().trim()}
                    onClick={() => setProfileSql(sqlB())} title="Profile query B — per-stage time breakdown">⏱ Profile</button>
          </div>
          <CodeMirrorSQL value={sqlB()} onInput={setSqlB} maxHeight="200px" lineWrap={false} schema={sqlSchema} />
        </div>
      </div>

      <Show when={profileSql() != null}>
        <QueryProfiler connectionId={connId()} database={database()} sql={profileSql()} onClose={() => setProfileSql(null)} />
      </Show>

      <Show when={result()}>
        {/* Equivalence verdict */}
        <div class="qc-equiv">
          <Show when={result().equivalence?.checked} fallback={
            <span class="qc-equiv-unknown">≈ Equivalence not checked — {result().equivalence?.note}</span>
          }>
            <Show when={result().equivalence.equal} fallback={
              <span class="qc-equiv-diff">✗ Result sets DIFFER ({result().equivalence.rowsA} vs {result().equivalence.rowsB} rows)</span>
            }>
              <span class="qc-equiv-same">✓ Result sets are identical ({result().equivalence.rowsA} rows{result().equivalence.snapshot ? ', same snapshot' : ''})</span>
            </Show>
          </Show>
        </div>

        {/* Timing */}
        <div class="qc-timing">
          <TimingCard side="A" cls="a" data={result().a} winner={faster() === 'a'} />
          <div class="qc-vs">
            <Show when={faster() === 'tie'}><span class="muted">tie</span></Show>
            <Show when={faster() === 'a' || faster() === 'b'}>
              <span class="qc-speedup">{faster() === 'a' ? 'A' : 'B'} faster<br />{speedup()}</span>
            </Show>
          </div>
          <TimingCard side="B" cls="b" data={result().b} winner={faster() === 'b'} />
        </div>

        {/* Handler deltas */}
        <Show when={handlerKeys().length}>
          <div class="qc-section">
            <div class="qc-section-title">Handler counters (one run) — storage-engine work done</div>
            <table class="qc-handler">
              <thead><tr><th>Counter</th><th>A</th><th>B</th></tr></thead>
              <tbody>
                <For each={handlerKeys()}>
                  {(k) => {
                    const a = result().a?.handler?.[k] || 0, b = result().b?.handler?.[k] || 0;
                    return (
                      <tr>
                        <td class="mono">{k}</td>
                        <td classList={{ 'qc-hl-worse': a > b }}>{a.toLocaleString()}</td>
                        <td classList={{ 'qc-hl-worse': b > a }}>{b.toLocaleString()}</td>
                      </tr>
                    );
                  }}
                </For>
              </tbody>
            </table>
          </div>
        </Show>

        {/* EXPLAIN side by side */}
        <div class="qc-explains">
          <ExplainCard side="A" data={result().a} />
          <ExplainCard side="B" data={result().b} />
        </div>
      </Show>
    </div>
  );
}

function TimingCard(props) {
  return (
    <div class={`qc-tcard qc-tcard-${props.cls}`} classList={{ 'qc-winner': props.winner }}>
      <div class="qc-tcard-head">Query {props.side}<Show when={props.winner}> 🏆</Show></div>
      <Show when={props.data?.error} fallback={
        <>
          <div class="qc-tmetric"><span class="qc-tmetric-v">{fmtMs(props.data?.median)}</span><span class="muted">ms median</span></div>
          <div class="qc-tsub muted">
            min {fmtMs(props.data?.min)} · avg {fmtMs(props.data?.avg)} ms · {(props.data?.rows ?? 0).toLocaleString()} rows
          </div>
        </>
      }>
        <div class="qc-terror mono">✗ {props.data.error}</div>
      </Show>
    </div>
  );
}

function ExplainCard(props) {
  const ex = () => props.data?.explain;
  return (
    <div class="qc-section qc-explain">
      <div class="qc-section-title">EXPLAIN {props.side}</div>
      <Show when={ex()} fallback={<div class="muted qc-noexplain">EXPLAIN unavailable.</div>}>
        <div class="qc-explain-wrap">
          <table class="qc-explain-table">
            <thead><tr><For each={ex().columns}>{(c) => <th>{c}</th>}</For></tr></thead>
            <tbody>
              <For each={ex().rows}>
                {(row) => <tr><For each={row}>{(cell) => <td class="mono">{cell}</td>}</For></tr>}
              </For>
            </tbody>
          </table>
        </div>
      </Show>
    </div>
  );
}
